import React, { useEffect, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  titleExtra?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  titleExtra,
  children,
  footer,
  maxWidth = '500px'
}) => {
  // Bloquer le scroll du body et cacher la navbar sur mobile quand le modal est ouvert
  useEffect(() => {
    const navbar = document.querySelector('.navbar');
    const searchBar = document.querySelector('.main-search-bar');
    const searchToggleButton = document.querySelector('.search-toggle-button');

    if (isOpen) {
      document.body.style.overflow = 'hidden';

      // Cacher navbar, barre de recherche et bouton loupe sur mobile
      if (window.innerWidth <= 768) {
        if (navbar) (navbar as HTMLElement).style.display = 'none';
        if (searchBar) (searchBar as HTMLElement).style.display = 'none';
        if (searchToggleButton) (searchToggleButton as HTMLElement).style.display = 'none';
      }
    } else {
      document.body.style.overflow = '';

      // Réafficher navbar, barre de recherche et bouton loupe
      if (navbar) (navbar as HTMLElement).style.display = '';
      if (searchBar) (searchBar as HTMLElement).style.display = '';
      if (searchToggleButton) (searchToggleButton as HTMLElement).style.display = '';
    }

    return () => {
      document.body.style.overflow = '';
      if (navbar) (navbar as HTMLElement).style.display = '';
      if (searchBar) (searchBar as HTMLElement).style.display = '';
      if (searchToggleButton) (searchToggleButton as HTMLElement).style.display = '';
    };
  }, [isOpen]);

  // Fermer avec la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth }}
      >
        {/* Header Desktop */}
        <div className="modal-header-section modal-header-desktop">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <h2 className="modal-title">{title}</h2>
            {titleExtra}
          </div>
          <button className="modal-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Header Mobile */}
        <div className="modal-header-section modal-header-mobile">
          <button className="modal-close-button modal-close-left" onClick={onClose}>
            ×
          </button>
          <div className="modal-header-right">
            {footer}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="modal-body-section">
          {children}
        </div>

        {/* Footer Desktop only */}
        {footer && (
          <div className="modal-footer-section modal-footer-desktop">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Utiliser un portal pour rendre le modal au niveau du body
  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;
